// Per-document field config — HARDCODED in the frontend (no backend round-trip).
// For each document type the wizard shows exactly these fields in step 2:
//   • NID-sourced fields (ids match the OCR output, so a scan auto-fills them)
//   • extra "manual" fields the person types (reasons, purpose, the corrected value…)
// The same field list is sent to the backend so the cached AI template emits a
// {field_id} token for each one; on "Generate" a plain script swaps {field_id} →
// the value (or a [Label] placeholder when still empty). No AI at replace time.
//
// Field shape: { id, type: 'text'|'textarea'|'date', en, bn }. A field whose id is in
// NID_FIELD_IDS is auto-fillable from the NID scan; everything else is manual.

// NID atoms — ids/labels mirror backend NID_FIELDS (src/config/aiDocumentTemplates.js).
const A = {
  name_bn: { type: 'text', en: 'Name (Bangla)', bn: 'নাম (বাংলা)' },
  name_en: { type: 'text', en: 'Name (English)', bn: 'নাম (ইংরেজি)' },
  father_name: { type: 'text', en: "Father's Name", bn: 'পিতার নাম' },
  mother_name: { type: 'text', en: "Mother's Name", bn: 'মাতার নাম' },
  date_of_birth: { type: 'date', en: 'Date of Birth', bn: 'জন্ম তারিখ' },
  nid_number: { type: 'text', en: 'NID Number', bn: 'জাতীয় পরিচয়পত্র নম্বর' },
  blood_group: { type: 'text', en: 'Blood Group', bn: 'রক্তের গ্রুপ' },
  village: { type: 'text', en: 'Village / Road', bn: 'গ্রাম/রাস্তা' },
  post_office: { type: 'text', en: 'Post Office', bn: 'ডাকঘর' },
  upazila: { type: 'text', en: 'Upazila / Thana', bn: 'উপজেলা/থানা' },
  district: { type: 'text', en: 'District', bn: 'জেলা' },
};

export const NID_FIELD_IDS = Object.keys(A);
const isNidField = (id) => Object.prototype.hasOwnProperty.call(A, id);

// Expand a list of NID atom ids into full field objects.
const nid = (...ids) => ids.map((id) => ({ id, ...A[id] }));

// NID fields scoped to ONE person of a multi-person document: the id is prefixed
// (e.g. groom_name_bn) and the label is person-qualified, plus a `nidField`
// back-reference to the generic OCR id so that person's scan can be mapped onto
// their own fields — two people's info never overwrite each other.
const personNid = (person, labelEn, labelBn, ...ids) =>
  ids.map((id) => ({
    id: `${person}_${id}`,
    type: A[id].type,
    en: `${labelEn} — ${A[id].en}`,
    bn: `${labelBn} — ${A[id].bn}`,
    person,
    nidField: id,
  }));

// Common NID subsets used across many documents.
const CORE = ['name_bn', 'father_name', 'mother_name', 'date_of_birth', 'nid_number', 'village', 'post_office', 'upazila', 'district'];
const CORE_EN = ['name_bn', 'name_en', 'father_name', 'mother_name', 'date_of_birth', 'nid_number', 'village', 'post_office', 'upazila', 'district'];
const ADDR = ['name_bn', 'father_name', 'nid_number', 'village', 'post_office', 'upazila', 'district'];

// Reusable manual fields.
const f = {
  reason: { id: 'reason', type: 'textarea', en: 'Reason', bn: 'কারণ' },
  purpose: { id: 'purpose', type: 'text', en: 'Purpose (what it is for)', bn: 'কী প্রয়োজনে (উদ্দেশ্য)' },
  subject: { id: 'subject', type: 'text', en: 'Subject', bn: 'বিষয়' },
  statement: { id: 'statement', type: 'textarea', en: 'Statement / details', bn: 'বিবৃতি / বিস্তারিত' },
  body: { id: 'body', type: 'textarea', en: 'Details / body', bn: 'বিস্তারিত / মূল বক্তব্য' },
  authority: { id: 'authority', type: 'text', en: 'To whom (authority / office)', bn: 'কার বরাবর (কর্তৃপক্ষ / অফিস)' },
};

// ── Per-document field lists ──────────────────────────────────────────────────
export const DOC_FIELDS = {
  // —— Identity & correction ——
  name_correction: [
    ...nid(...CORE_EN),
    { id: 'correct_name', type: 'text', en: 'Correct full name (as it should be)', bn: 'সঠিক পূর্ণ নাম (যেভাবে হবে)' },
    { id: 'document_name', type: 'text', en: 'Which record/certificate', bn: 'কোন রেকর্ড/সনদে' },
    f.reason,
  ],
  dob_correction: [
    ...nid(...CORE),
    { id: 'correct_dob', type: 'date', en: 'Correct date of birth', bn: 'সঠিক জন্ম তারিখ' },
    { id: 'document_name', type: 'text', en: 'Which record/certificate', bn: 'কোন রেকর্ড/সনদে' },
    f.reason,
  ],
  birth_cert_correction: [
    ...nid('name_bn', 'name_en', 'father_name', 'mother_name', 'date_of_birth', 'village', 'post_office', 'upazila', 'district'),
    { id: 'birth_reg_no', type: 'text', en: 'Birth registration number', bn: 'জন্ম নিবন্ধন নম্বর' },
    { id: 'correct_info', type: 'text', en: 'Correct information', bn: 'সঠিক তথ্য' },
    f.reason,
  ],
  nid_info_correction: [
    ...nid('name_bn', 'name_en', 'father_name', 'mother_name', 'date_of_birth', 'nid_number', 'blood_group', 'village', 'post_office', 'upazila', 'district'),
    { id: 'correction_details', type: 'textarea', en: 'What to correct (field & correct value)', bn: 'কী সংশোধন করবেন (তথ্য ও সঠিক মান)' },
  ],
  name_difference_declaration: [
    ...nid('name_bn', 'name_en', 'father_name', 'mother_name', 'date_of_birth', 'nid_number'),
    { id: 'other_name', type: 'text', en: 'Other name (as on the other document)', bn: 'অন্য নাম (অন্য ডকুমেন্টে যেভাবে আছে)' },
    { id: 'other_document', type: 'text', en: 'Which document has the other name', bn: 'কোন ডকুমেন্টে অন্য নামটি আছে' },
  ],
  same_person: [
    ...nid('name_bn', 'name_en', 'father_name', 'mother_name', 'date_of_birth', 'nid_number'),
    { id: 'other_name', type: 'text', en: 'Other name used', bn: 'ব্যবহৃত অন্য নাম' },
    { id: 'context', type: 'textarea', en: 'Where/why both names are used', bn: 'কোথায়/কেন দুটি নাম ব্যবহৃত' },
  ],
  identity_declaration: [
    ...nid(...CORE_EN),
    f.purpose,
  ],
  address_change: [
    ...nid('name_bn', 'father_name', 'nid_number', 'village', 'post_office', 'upazila', 'district'),
    { id: 'new_address', type: 'textarea', en: 'New address', bn: 'নতুন ঠিকানা' },
    f.reason,
  ],

  // —— Affidavit & declaration ——
  general_affidavit: [...nid(...CORE), f.subject, f.statement],
  general_declaration: [...nid(...ADDR), f.subject, f.statement],
  age_affidavit: [...nid('name_bn', 'father_name', 'mother_name', 'date_of_birth', 'nid_number', 'village', 'post_office', 'upazila', 'district'), f.purpose],
  single_status: [...nid(...CORE_EN), f.purpose],
  nationality_declaration: [...nid('name_bn', 'father_name', 'mother_name', 'date_of_birth', 'nid_number', 'village', 'post_office', 'upazila', 'district'), f.purpose],
  permanent_address_declaration: [
    ...nid('name_bn', 'father_name', 'nid_number', 'village', 'post_office', 'upazila', 'district'),
    { id: 'permanent_address', type: 'textarea', en: 'Permanent address (if different from NID)', bn: 'স্থায়ী ঠিকানা (NID থেকে ভিন্ন হলে)' },
    f.purpose,
  ],
  info_truth_declaration: [...nid('name_bn', 'father_name', 'nid_number'), f.subject, f.statement],
  undertaking: [
    ...nid(...ADDR),
    f.subject,
    { id: 'undertaking_text', type: 'textarea', en: 'What you promise / undertake', bn: 'আপনি কী অঙ্গীকার করছেন' },
  ],

  // —— Official applications ——
  general_application: [...nid(...ADDR), f.authority, f.subject, f.body],
  dorkhasto: [...nid(...ADDR), f.authority, f.subject, f.body],
  noc: [...nid(...ADDR), f.authority, { id: 'purpose', type: 'textarea', en: 'Purpose of the NOC', bn: 'অনাপত্তির উদ্দেশ্য' }],
  permission_letter: [...nid(...ADDR), f.authority, { id: 'purpose', type: 'textarea', en: 'What permission is needed', bn: 'কী অনুমতি প্রয়োজন' }],
  prottoyon: [...nid(...CORE_EN), f.purpose],
  id_card_application: [...nid(...CORE_EN), f.authority, f.purpose],
  address_certificate: [...nid(...ADDR), f.purpose],
  nationality_certificate: [...nid('name_bn', 'father_name', 'mother_name', 'date_of_birth', 'nid_number', 'village', 'post_office', 'upazila', 'district'), f.purpose],

  // —— Lost documents ——
  lost_document: [
    ...nid(...ADDR),
    { id: 'lost_item', type: 'text', en: 'What was lost', bn: 'কী হারিয়েছে' },
    { id: 'lost_details', type: 'textarea', en: 'When & where it was lost', bn: 'কখন ও কোথায় হারিয়েছে' },
  ],
  lost_nid_application: [
    ...nid(...CORE_EN),
    { id: 'lost_details', type: 'textarea', en: 'When & where the NID was lost', bn: 'কখন ও কোথায় NID হারিয়েছে' },
  ],
  lost_birth_certificate: [
    ...nid('name_bn', 'father_name', 'mother_name', 'date_of_birth', 'village', 'post_office', 'upazila', 'district'),
    { id: 'birth_reg_no', type: 'text', en: 'Birth registration number (if known)', bn: 'জন্ম নিবন্ধন নম্বর (জানা থাকলে)' },
    { id: 'lost_details', type: 'textarea', en: 'When & where it was lost', bn: 'কখন ও কোথায় হারিয়েছে' },
  ],
  lost_certificate: [
    ...nid('name_bn', 'father_name', 'nid_number'),
    { id: 'certificate_name', type: 'text', en: 'Which certificate', bn: 'কোন সার্টিফিকেট' },
    { id: 'lost_details', type: 'textarea', en: 'When & where it was lost', bn: 'কখন ও কোথায় হারিয়েছে' },
  ],

  // —— Family & relations ——
  relationship_declaration: [
    ...nid('name_bn', 'father_name', 'mother_name', 'date_of_birth', 'nid_number', 'village', 'post_office', 'upazila', 'district'),
    { id: 'relative_name', type: 'text', en: "Relative's name", bn: 'সম্পর্কিত ব্যক্তির নাম' },
    { id: 'relationship', type: 'text', en: 'Relationship', bn: 'সম্পর্ক' },
    f.purpose,
  ],

  // —— Job & personal ——
  job_application: [
    ...nid('name_bn', 'name_en', 'father_name', 'nid_number', 'date_of_birth', 'village', 'post_office', 'upazila', 'district'),
    { id: 'position', type: 'text', en: 'Position applied for', bn: 'যে পদে আবেদন' },
    { id: 'company', type: 'text', en: 'Company / organization', bn: 'প্রতিষ্ঠান' },
    f.body,
  ],
  personal_info_form: [
    ...nid('name_bn', 'name_en', 'father_name', 'mother_name', 'date_of_birth', 'nid_number', 'blood_group', 'village', 'post_office', 'upazila', 'district'),
    { id: 'phone', type: 'text', en: 'Phone number', bn: 'ফোন নম্বর' },
    { id: 'email', type: 'text', en: 'Email (optional)', bn: 'ইমেইল (ঐচ্ছিক)' },
  ],
  biodata: [
    ...nid('name_bn', 'name_en', 'father_name', 'mother_name', 'date_of_birth', 'nid_number', 'blood_group', 'village', 'post_office', 'upazila', 'district'),
    { id: 'education', type: 'text', en: 'Education', bn: 'শিক্ষাগত যোগ্যতা' },
    { id: 'occupation', type: 'text', en: 'Occupation', bn: 'পেশা' },
    { id: 'phone', type: 'text', en: 'Phone number', bn: 'ফোন নম্বর' },
  ],

  // —— Other personal ——
  consent_letter: [
    ...nid(...ADDR),
    { id: 'consent_for', type: 'textarea', en: 'What you are consenting to', bn: 'কীসে সম্মতি দিচ্ছেন' },
  ],

  // —— Added 2026-09-17 ——
  hawlat_undertaking: [
    ...nid(...ADDR),
    { id: 'lender_name', type: 'text', en: 'Lender name (who you borrowed from)', bn: 'যার কাছ থেকে নিয়েছেন (পাওনাদার)' },
    { id: 'amount', type: 'text', en: 'Amount taken', bn: 'গৃহীত টাকার পরিমাণ' },
    { id: 'repay_date', type: 'date', en: 'Repayment date', bn: 'ফেরত দেওয়ার তারিখ' },
    { id: 'terms', type: 'textarea', en: 'Terms / conditions', bn: 'শর্তাবলি' },
  ],
  assistance_certificate: [
    ...nid(...CORE),
    { id: 'assistance_for', type: 'textarea', en: 'What assistance is needed', bn: 'কী সাহায্য প্রয়োজন' },
    f.authority,
  ],
  resignation_letter: [
    ...nid(...ADDR),
    { id: 'organization', type: 'text', en: 'Organization / committee', bn: 'প্রতিষ্ঠান / কমিটি' },
    { id: 'position', type: 'text', en: 'Your position (e.g. President)', bn: 'আপনার পদ (যেমন সভাপতি)' },
    { id: 'effective_date', type: 'date', en: 'Resignation effective date', bn: 'পদত্যাগ কার্যকরের তারিখ' },
    f.reason,
    f.authority,
  ],
  construction_contract: [
    ...personNid('owner', 'Owner', 'মালিক', 'name_bn', 'father_name', 'nid_number', 'village', 'post_office', 'upazila', 'district'),
    ...personNid('mason', 'Mason', 'রাজমিস্ত্রি', 'name_bn', 'father_name', 'nid_number', 'village', 'post_office', 'upazila', 'district'),
    { id: 'work_description', type: 'textarea', en: 'Work description', bn: 'কাজের বিবরণ' },
    { id: 'rate', type: 'text', en: 'Rate / total cost', bn: 'রেট / মোট খরচ' },
    { id: 'timeline', type: 'text', en: 'Timeline / completion', bn: 'সময়সীমা / সম্পন্নের সময়' },
    { id: 'terms', type: 'textarea', en: 'Terms & conditions', bn: 'শর্তাবলি' },
  ],
  death_certificate_muslim: [
    ...nid(...ADDR),
    { id: 'deceased_name', type: 'text', en: 'Name of the deceased', bn: 'মৃত ব্যক্তির নাম' },
    { id: 'deceased_father', type: 'text', en: "Deceased's father/husband name", bn: 'মৃত ব্যক্তির পিতা/স্বামীর নাম' },
    { id: 'date_of_death', type: 'date', en: 'Date of death', bn: 'মৃত্যুর তারিখ' },
    { id: 'place_of_death', type: 'text', en: 'Place of death', bn: 'মৃত্যুর স্থান' },
    { id: 'age_at_death', type: 'text', en: 'Age at death', bn: 'মৃত্যুকালীন বয়স' },
    { id: 'relation_to_deceased', type: 'text', en: 'Your relation to the deceased', bn: 'মৃত ব্যক্তির সাথে সম্পর্ক' },
  ],
  death_certificate_other: [
    ...nid(...ADDR),
    { id: 'deceased_name', type: 'text', en: 'Name of the deceased', bn: 'মৃত ব্যক্তির নাম' },
    { id: 'deceased_father', type: 'text', en: "Deceased's father/husband name", bn: 'মৃত ব্যক্তির পিতা/স্বামীর নাম' },
    { id: 'religion', type: 'text', en: 'Religion', bn: 'ধর্ম' },
    { id: 'date_of_death', type: 'date', en: 'Date of death', bn: 'মৃত্যুর তারিখ' },
    { id: 'place_of_death', type: 'text', en: 'Place of death', bn: 'মৃত্যুর স্থান' },
    { id: 'age_at_death', type: 'text', en: 'Age at death', bn: 'মৃত্যুকালীন বয়স' },
    { id: 'relation_to_deceased', type: 'text', en: 'Your relation to the deceased', bn: 'মৃত ব্যক্তির সাথে সম্পর্ক' },
  ],
  muslim_marriage_registration: [
    ...personNid('groom', 'Groom', 'বর', 'name_bn', 'father_name', 'nid_number', 'village', 'post_office', 'upazila', 'district'),
    ...personNid('bride', 'Bride', 'কনে', 'name_bn', 'father_name', 'nid_number', 'village', 'post_office', 'upazila', 'district'),
    { id: 'marriage_date', type: 'date', en: 'Date of marriage', bn: 'বিবাহের তারিখ' },
    { id: 'kabin_amount', type: 'text', en: 'Kabin (dower) amount', bn: 'কাবিন/দেনমোহরের পরিমাণ' },
    { id: 'marriage_place', type: 'text', en: 'Place of marriage', bn: 'বিবাহের স্থান' },
    { id: 'witnesses', type: 'textarea', en: 'Witnesses', bn: 'সাক্ষীগণ' },
  ],
  fee_reduction_application: [
    ...nid('name_bn', 'father_name', 'nid_number', 'village', 'post_office', 'upazila', 'district'),
    { id: 'institution', type: 'text', en: 'Institution name', bn: 'প্রতিষ্ঠানের নাম' },
    { id: 'class_grade', type: 'text', en: 'Class / grade', bn: 'শ্রেণি' },
    { id: 'current_fee', type: 'text', en: 'Current fee / tuition', bn: 'বর্তমান ফি / বেতন' },
    { id: 'requested_fee', type: 'text', en: 'Requested (reduced) amount', bn: 'যে পরিমাণ চাইছেন' },
    f.reason,
    f.authority,
  ],
};

// Fallback for a free-text / unknown type (user described instead of picking a card).
const DEFAULT_FIELDS = [...nid(...CORE), f.subject, f.body];

// Multi-person documents: each person gets their OWN NID upload slot in step 2, and
// that slot's scan fills ONLY that person's prefixed fields (see personNid) — so two
// people's info never overwrite each other. `key` matches the personNid() prefix.
export const DOC_PERSONS = {
  muslim_marriage_registration: [
    { key: 'groom', en: "Groom's NID", bn: 'বরের এনআইডি' },
    { key: 'bride', en: "Bride's NID", bn: 'কনের এনআইডি' },
  ],
  construction_contract: [
    { key: 'owner', en: "Owner's NID", bn: 'মালিকের এনআইডি' },
    { key: 'mason', en: "Mason's NID", bn: 'রাজমিস্ত্রির এনআইডি' },
  ],
};

// The persons of a multi-person doc (with language-resolved slot labels), or null
// for a normal single-person document.
export function getDocPersons(type, lang) {
  const k = lang === 'bn' ? 'bn' : 'en';
  const persons = type && DOC_PERSONS[type];
  if (!persons || !persons.length) return null;
  return persons.map((p) => ({ key: p.key, label: p[k] || p.en }));
}

// Resolve a document's fields in the given language:
// → [{ id, type, label, source: 'nid'|'manual', person, nidField }]
// `person` = which NID slot fills it (multi-person docs); `nidField` = the generic
// OCR field id it maps from (so a scan is routed to the right person's field).
export function resolveDocFields(type, lang) {
  const k = lang === 'bn' ? 'bn' : 'en';
  const list = (type && DOC_FIELDS[type]) || DEFAULT_FIELDS;
  return list.map((fd) => {
    const nidField = fd.nidField || (isNidField(fd.id) ? fd.id : null);
    return {
      id: fd.id,
      type: fd.type || 'text',
      label: fd[k] || fd.en || fd.id,
      source: nidField ? 'nid' : 'manual',
      person: fd.person || null,
      nidField,
    };
  });
}
